import * as React from "react";
import { AccessUser, Branch, Region } from "@/interfaces";
import { useEffect, useMemo, useState } from "react";
import { EligibilityRule } from "./components/EligibilityRule";
import { EligibilityRadio } from "./components/EligibilityRadio";
import { CoachService, AssistantServices, AccessType, GenericAccess } from "@/services";
import { AccessListModal, AccessUserItem } from "./components/AccessListModal";
import { useSocialCoachSession, usePlayerSearch } from "@/utils";
import { SegmentOption, SegmentSelectionModal } from "./components/SegmentSelectionModal";
import { PlayerSelectionModal } from "./components/PlayerSelectionModal";
import { EligibilityCriterion, EligibilityLevel, PlayerSearchQuery } from "@/services/bedrock";
import { Spinner, Table, Button } from "@/components/ui";
import { Users, Settings, ShieldUser } from "lucide-react";

interface OwnProps {
  originalAccess: GenericAccess;
  currentAccess: GenericAccess;
  accessType: AccessType;
  updateHandler: (newCriteria: EligibilityCriterion[]) => Promise<any>;
  isNew?: boolean;
}

type Props = OwnProps;

const EligibilityEditorFC: React.FC<Props> = ({ originalAccess, currentAccess, accessType, updateHandler, isNew }) => {
  const { coachId, userId } = useSocialCoachSession();
  const [loading, setLoading] = useState(false);
  const [criterion, setCriterion] = useState<EligibilityCriterion[]>(originalAccess.eligibilityCriteria || []);
  const [showRules, setShowRules] = useState(!!originalAccess.eligibilityCriteria?.length);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const [showAssistantList, setShowAssistantList] = useState(false);
  const [showRegionChooser, setShowRegionChooser] = useState(false);
  const [showBranchChooser, setShowBranchChooser] = useState(false);
  const [showPlayerChooser, setShowPlayerChooser] = useState(false);
  const [playerList, setPlayerList] = useState<AccessUserItem[] | undefined>();
  const [assistants, setAssistants] = useState<AccessUserItem[] | undefined>();
  const [branches, setBranches] = useState<Branch[] | undefined>();
  const [regions, setRegions] = useState<Region[] | undefined>();
  const [regionsLoading, setRegionsLoading] = useState<boolean>(false);
  const [branchesLoading, setBranchesLoading] = useState<boolean>(false);

  const [eligiblePlayersPage, setEligiblePlayersPage] = useState(1);
  const [allEligiblePlayers, setAllEligiblePlayers] = useState<any[]>([]);

  const usersWithAccess = useMemo(
    () => currentAccess.accessUsers?.filter(a => a.hasReadAccess),
    [currentAccess.accessUsers]
  );
  const originalUsersWithAccess = useMemo(
    () => originalAccess.accessUsers?.filter(a => a.hasReadAccess),
    [originalAccess.accessUsers]
  );
  const hasEligibilityAccess = useMemo(() => {
    return isNew || (!!userId && !!originalUsersWithAccess?.some(u => u.userId == userId && u.hasEligibilityAccess));
  }, [userId, originalUsersWithAccess, isNew]);

  useEffect(() => {
    setCriterion(originalAccess.eligibilityCriteria || []);
  }, [originalAccess.eligibilityCriteria]);

  useEffect(() => {
    setShowRules(!!originalAccess?.eligibilityCriteria?.length);
  }, [originalAccess?.eligibilityCriteria?.length]);

  useEffect(() => {
    setEligiblePlayersPage(1);
    setAllEligiblePlayers([]);
  }, [currentAccess.eligiblePlayerIds]);

  useEffect(() => {
    loadBranches().catch(console.error);
    loadRegions().catch(console.error);
  }, [coachId]);

  useEffect(() => {
    loadAssistants(usersWithAccess || []).catch(console.error);
  }, [usersWithAccess]);

  const { searchPlayers } = usePlayerSearch();
  const searchParams: PlayerSearchQuery = useMemo(() => {
    const ids = currentAccess.eligiblePlayerIds || [];
    return {
      page: eligiblePlayersPage,
      perPage: 100,
      planStatuses: ["ACTIVE", "FREE_TRIAL"],
      playerIdsOrSegmentation: true,
      playerIds: ids.length > 0 ? ids : [-1],
    };
  }, [currentAccess.eligiblePlayerIds, eligiblePlayersPage]);

  const eligiblePlayerIdsCount = (currentAccess.eligiblePlayerIds || []).length;
  const { players: searchData, isFetching: isSearchingPlayers } = searchPlayers(
    searchParams,
    eligiblePlayerIdsCount > 0
  );

  useEffect(() => {
    if (searchData?.content) {
      if (searchData.pagination?.page === eligiblePlayersPage) {
        if (eligiblePlayersPage === 1) {
          setAllEligiblePlayers(searchData.content);
        } else {
          setAllEligiblePlayers(prev => {
            const newPlayers = searchData.content.filter(
              (p: any) => !prev.some((existing: any) => existing.id === p.id)
            );
            return [...prev, ...newPlayers];
          });
        }

        const totalPages = searchData.pagination?.totalPages || 1;
        if (eligiblePlayersPage < totalPages) {
          setEligiblePlayersPage(prev => prev + 1);
        }
      }
    }
  }, [searchData, eligiblePlayersPage]);

  useEffect(() => {
    setPlayerList(
      allEligiblePlayers.map(player => {
        const branchNames =
          player.segmentationDetails?.filter((s: any) => s.type === "BRANCH")?.map((s: any) => s.name) || [];
        const regionNames =
          player.segmentationDetails?.filter((s: any) => s.type === "REGION")?.map((s: any) => s.name) || [];
        const stateCodes =
          player.segmentationDetails?.filter((s: any) => s.type === "STATE")?.map((s: any) => s.name) || [];

        return {
          name: player.fullName || `${player.firstName || ""} ${player.lastName || ""}`.trim() || "Unknown",
          email: player.email,
          regionNames,
          branchNames,
          stateCodes,
        } as AccessUserItem;
      })
    );
  }, [allEligiblePlayers]);

  const isPlayersLoading =
    isSearchingPlayers || (searchData?.pagination && eligiblePlayersPage < (searchData.pagination.totalPages || 1));

  const loadAssistants = async (users: AccessUser[]) => {
    const userIds = users.map(a => a.userId);
    const allAssistants = await AssistantServices.getByCoachId(coachId?.toString()!);
    const assistants = allAssistants.filter(a => userIds.includes(a.userId));
    setAssistants(
      users.map(accessUser => {
        const assistant = assistants.find(a => a.userId == accessUser.userId);
        return {
          name: accessUser.name,
          email: accessUser.email,
          regionNames: assistant?.regionNames,
          branchNames: assistant?.branchNames,
          readAccess: accessUser.hasReadAccess,
          writeAccess: accessUser.hasWriteAccess,
          eligibilityAccess: accessUser.hasEligibilityAccess,
        } as AccessUserItem;
      })
    );
  };

  const loadBranches = async () => {
    setBranchesLoading(true);
    const list = await CoachService.branches(coachId?.toString()!);
    setBranches(list);
    setBranchesLoading(false);
  };

  const loadRegions = async () => {
    setRegionsLoading(true);
    const list = await CoachService.regions(coachId?.toString()!);
    setRegions(list);
    setRegionsLoading(false);
  };

  const updateCriterion = async (newCriterion: EligibilityCriterion[]) => {
    setCriterion(newCriterion);
    setLoading(true);
    await updateHandler(newCriterion);
    setLoading(false);
  };

  const diffString = (n1?: number, n2?: number): string => {
    const diff = (n1 || 0) - (n2 || 0);
    if (diff < 0) return `${n1} (${diff})`;
    else if (diff > 0) return `${n1} (+${diff})`;
    else return `${n1}`;
  };

  const typeLabel = useMemo(() => {
    switch (accessType) {
      case "CAMPAIGNS":
        return "Campaign";
      case "LIBRARY":
        return "Prompt";
      case "VIDEO_PAGES":
        return "Super Page";
      case "FORM":
        return "Form";
    }
  }, [accessType]);

  const playerCount = useMemo(() => {
    return diffString(currentAccess.eligiblePlayerIds?.length || 0, originalAccess.eligiblePlayerIds?.length || 0);
  }, [currentAccess.eligiblePlayerIds?.length, originalAccess.eligiblePlayerIds?.length]);

  const assistantCount = useMemo(() => {
    return diffString(usersWithAccess?.length || 0, originalUsersWithAccess?.length || 0);
  }, [usersWithAccess?.length, originalUsersWithAccess?.length]);

  const hasChanges = useMemo(() => {
    return (
      usersWithAccess?.length != originalUsersWithAccess?.length ||
      currentAccess.eligiblePlayerIds?.length != originalAccess.eligiblePlayerIds?.length
    );
  }, [
    currentAccess.eligiblePlayerIds?.length,
    originalAccess.eligiblePlayerIds?.length,
    usersWithAccess?.length,
    originalUsersWithAccess?.length,
  ]);

  const updateRule = (newRule: EligibilityCriterion) => {
    const otherRules = criterion.filter(r => r.level != newRule.level);
    updateCriterion([...otherRules, newRule]).catch(console.error);
  };

  const clearRule = (level: EligibilityLevel) => {
    const otherRules = criterion.filter(r => r.level != level);
    updateCriterion([...otherRules]).catch(console.error);
  };

  const branchRule = useMemo(() => criterion.find(c => c.level == "BRANCH"), [criterion]);
  const branchTags = useMemo(() => {
    return (branchRule?.values || []).map(
      bid => branches?.find(b => b.branchId == bid.toString())?.name || `Branch ${bid}`
    );
  }, [branches, branchRule?.values]);
  const branchOptions = useMemo(() => {
    return (
      branches?.map(b => {
        return {
          id: Number(b.branchId),
          level: "BRANCH",
          name: b.name,
        } as SegmentOption;
      }) || []
    );
  }, [branches]);

  const regionRule = useMemo(() => criterion.find(c => c.level == "REGION"), [criterion]);
  const regionTags = useMemo(() => {
    return (regionRule?.values || []).map(
      rid => regions?.find(r => r.regionId == rid.toString())?.name || `Region ${rid}`
    );
  }, [regions, regionRule?.values]);
  const regionOptions = useMemo(() => {
    return (
      regions?.map(r => {
        return {
          id: Number(r.regionId),
          level: "REGION",
          name: r.name,
        } as SegmentOption;
      }) || []
    );
  }, [regions]);

  const playerRule = useMemo(() => criterion.find(c => c.level == "PLAYER_ID"), [criterion]);
  const playerTags = useMemo(() => {
    const maxToShow = 5;
    const playerCount = playerRule?.values?.length || 0;
    const playersToShow = (playerRule?.values || []).slice(0, maxToShow);
    const showMoreTag = playerCount > maxToShow;
    const tags = playersToShow.map(pid => {
      const player = allEligiblePlayers?.find(p => p.id == pid);
      if (player) {
        return player.fullName || `${player.firstName || ""} ${player.lastName || ""}`.trim();
      }
      return `Player ${pid}`;
    });
    if (showMoreTag) tags.push(`+${playerCount - maxToShow} Others`);
    return tags;
  }, [allEligiblePlayers, playerRule?.values]);

  const updateRuleFlag = (currentRule: EligibilityCriterion | undefined, level: EligibilityLevel, flag: boolean) => {
    if (currentRule) updateRule({ ...currentRule, isInclude: flag });
    else
      updateRule({
        isInclude: flag,
        level,
        values: [],
      });
  };

  const updateSelectedValues = (
    currentRule: EligibilityCriterion | undefined,
    level: EligibilityLevel,
    values: number[]
  ) => {
    if (values.length == 0) {
      clearRule(level);
    } else if (currentRule) updateRule({ ...currentRule, values });
    else
      updateRule({
        isInclude: true,
        level,
        values,
      });
  };

  const updateShowRules = (b: boolean) => {
    if (b) {
      setShowRules(false);
      updateCriterion([]).catch(console.error);
    } else {
      setShowRules(true);
      updateCriterion(originalAccess?.eligibilityCriteria || []).catch(console.error);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* HEADER SECTION */}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          {loading && <Spinner className="size-4 text-slate-500 animate-spin" />}
        </div>
        {hasChanges && (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 animate-pulse">
            Unsaved changes
          </span>
        )}
      </div>

      {/* ACCESS SUMMARY DASHBOARD */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Players Card */}
        <div className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="flex items-center justify-center size-12 bg-primary/10 rounded-xl text-primary shrink-0">
              <Users className="size-5" />
            </div>
            <div>
              <div className="text-xs text-slate-400 font-medium leading-none mb-1">Eligible Players</div>
              <div className="text-lg font-extrabold text-slate-900 tracking-tight leading-none">{playerCount}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPlayerList(true)}
            className="text-xs font-semibold text-primary hover:bg-slate-50 h-8 border-slate-200 rounded-lg px-3"
          >
            View List
          </Button>
        </div>

        {/* Assistants Card */}
        <div className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="flex items-center justify-center size-12 bg-primary/10 rounded-xl text-primary shrink-0">
              <ShieldUser className="size-5" />
            </div>
            <div>
              <div className="text-xs text-slate-400 font-medium leading-none mb-1">Eligible Assistants</div>
              <div className="text-lg font-extrabold text-slate-900 tracking-tight leading-none">{assistantCount}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAssistantList(true)}
            className="text-xs font-semibold text-primary hover:bg-slate-50 h-8 border-slate-200 rounded-lg px-3"
          >
            View List
          </Button>
        </div>
      </div>

      {/* ELIGIBILITY RULES CONTROLS */}
      <div className="flex flex-col gap-4 p-5 bg-white border border-slate-100 rounded-2xl shadow-sm">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900 border-b border-slate-50 pb-2.5">
          <Settings className="size-4 text-slate-500" />
          Eligibility Rules
        </div>

        <EligibilityRadio
          name="primary-eligibility-toggle"
          included={!showRules}
          onUpdate={updateShowRules}
          trueLabel="Available to everyone"
          falseLabel="Set rules for eligibility"
          readOnly={!hasEligibilityAccess}
        />

        {showRules && (
          <div className="overflow-hidden border border-slate-100 rounded-xl mt-2 bg-slate-50/20">
            <Table>
              <Table.Header className="bg-slate-50/70 border-b border-slate-100">
                <Table.Row className="hover:bg-transparent">
                  <Table.Head className="w-[150px] font-semibold text-slate-600 text-xs py-3">TYPE</Table.Head>
                  <Table.Head className="w-[120px] font-semibold text-slate-600 text-xs py-3">RULE</Table.Head>
                  <Table.Head className="font-semibold text-slate-600 text-xs py-3">SELECTED VALUE(S)</Table.Head>
                </Table.Row>
              </Table.Header>

              <Table.Body className="bg-white">
                <EligibilityRule
                  level="REGION"
                  currentRule={regionRule}
                  tags={regionTags}
                  loading={regionsLoading}
                  readOnly={!hasEligibilityAccess}
                  onIncludeFlagChanged={f => updateRuleFlag(regionRule, "REGION", f)}
                  onUpdateClicked={() => setShowRegionChooser(true)}
                />

                <EligibilityRule
                  level="BRANCH"
                  currentRule={branchRule}
                  tags={branchTags}
                  loading={branchesLoading}
                  readOnly={!hasEligibilityAccess}
                  onIncludeFlagChanged={f => updateRuleFlag(branchRule, "BRANCH", f)}
                  onUpdateClicked={() => setShowBranchChooser(true)}
                />

                <EligibilityRule
                  level="PLAYER_ID"
                  currentRule={playerRule}
                  tags={playerTags}
                  loading={isPlayersLoading}
                  readOnly={!hasEligibilityAccess}
                  onIncludeFlagChanged={f => updateRuleFlag(playerRule, "PLAYER_ID", f)}
                  onUpdateClicked={() => setShowPlayerChooser(true)}
                />
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <AccessListModal
        title={`Players with access to this ${typeLabel}`}
        users={playerList || []}
        open={showPlayerList}
        onClose={() => setShowPlayerList(false)}
      />
      <AccessListModal
        title={`Assistants with access to this ${typeLabel}`}
        users={assistants || []}
        open={showAssistantList}
        onClose={() => setShowAssistantList(false)}
        showPermissions={true}
      />
      <SegmentSelectionModal
        open={showRegionChooser}
        level="REGION"
        options={regionOptions}
        values={regionRule?.values || []}
        onUpdate={values => {
          updateSelectedValues(regionRule, "REGION", values);
          setShowRegionChooser(false);
        }}
        onClose={() => setShowRegionChooser(false)}
      />
      <SegmentSelectionModal
        open={showBranchChooser}
        level="BRANCH"
        options={branchOptions}
        values={branchRule?.values || []}
        onUpdate={values => {
          updateSelectedValues(branchRule, "BRANCH", values);
          setShowBranchChooser(false);
        }}
        onClose={() => setShowBranchChooser(false)}
      />
      <PlayerSelectionModal
        open={showPlayerChooser}
        values={playerRule?.values || []}
        onUpdate={values => {
          updateSelectedValues(playerRule, "PLAYER_ID", values);
          setShowPlayerChooser(false);
        }}
        onClose={() => setShowPlayerChooser(false)}
      />
    </div>
  );
};

export const EligibilityEditor = EligibilityEditorFC;
